import React, { useState, useEffect, useRef } from 'react';
import api from '../api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ChevronDown, ChevronRight, Loader2, GitBranch, CheckCircle2, AlertTriangle, Sparkles } from 'lucide-react';

const BADGE_CLASS = {
  positive: 'bg-emerald-100 text-emerald-700',
  negative: 'bg-red-100 text-red-700',
  edge: 'bg-amber-100 text-amber-700',
  security: 'bg-violet-100 text-violet-700',
  validation: 'bg-blue-100 text-blue-700',
  other: 'bg-muted text-muted-foreground',
};

const ScenarioRow = ({ scenario }) => {
  const [open, setOpen] = useState(false);
  const badge = BADGE_CLASS[scenario.scenario_type] || BADGE_CLASS.other;
  const hasTCs = scenario.testcases.length > 0;
  const isReal = scenario.scenario_id !== null;
  const isMissing = isReal && !hasTCs;

  return (
    <div className={`ml-6 border-l-2 pl-4 py-1 ${isMissing ? 'border-red-300 bg-red-50/50' : 'border-azure/15'}`}>
      <button
        onClick={() => hasTCs && setOpen(o => !o)}
        className={`flex items-center gap-2 w-full text-left py-1 rounded ${hasTCs ? 'hover:bg-muted/50 cursor-pointer' : 'cursor-default'}`}
        data-testid={`trace-scenario-${scenario.scenario_id || 'unlinked'}`}
      >
        {hasTCs ? (
          open ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
        ) : (
          <span className="w-3.5" />
        )}
        {scenario.scenario_id && (
          <span className="font-mono text-xs text-muted-foreground">{scenario.scenario_id}</span>
        )}
        <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${badge}`}>{scenario.scenario_type}</span>
        <span className="text-sm text-slate-700 flex-1 truncate">{scenario.scenario_title}</span>
        {isReal && (
          hasTCs ? (
            <span className="flex items-center gap-1 text-xs text-emerald-600 whitespace-nowrap" data-testid={`scenario-covered-${scenario.scenario_id}`}>
              <CheckCircle2 className="h-3.5 w-3.5" /> Covered
            </span>
          ) : (
            <span className="flex items-center gap-1 text-xs text-red-600 whitespace-nowrap" data-testid={`scenario-missing-${scenario.scenario_id}`}>
              <AlertTriangle className="h-3.5 w-3.5" /> Missing
            </span>
          )
        )}
        <span className="text-xs text-muted-foreground whitespace-nowrap ml-1">{scenario.testcases.length} TC{scenario.testcases.length !== 1 ? 's' : ''}</span>
      </button>
      {open && (
        <div className="ml-6 mt-1 space-y-1 pb-1">
          {scenario.testcases.map(tc => (
            <div key={tc.test_case_id} className="flex items-center gap-2 text-sm py-0.5" data-testid={`trace-tc-${tc.test_case_id}`}>
              <span className="px-1.5 py-0.5 bg-azure/10 text-azure rounded font-mono text-xs font-medium">{tc.test_case_id}</span>
              <span className="text-slate-600 truncate">{tc.title}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const getCoverage = (scenarios) => {
  const real = scenarios.filter(s => s.scenario_id !== null);
  if (real.length === 0) return { total: 0, covered: 0, missing: 0, pct: 0 };
  const covered = real.filter(s => s.testcases.length > 0).length;
  return { total: real.length, covered, missing: real.length - covered, pct: Math.round((covered / real.length) * 100) };
};

const CoverageBadge = ({ pct }) => {
  const cls = pct === 100 ? 'bg-emerald-100 text-emerald-700' : pct >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700';
  return <span className={`px-2 py-0.5 rounded text-xs font-semibold ${cls}`} data-testid="coverage-badge">{pct}%</span>;
};

const RequirementRow = ({ item }) => {
  const [open, setOpen] = useState(false);
  const cov = getCoverage(item.scenarios);

  return (
    <Card className="overflow-hidden" data-testid={`trace-req-${item.requirement_id}`}>
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-3 w-full text-left p-4 hover:bg-muted/30 transition-colors"
        data-testid={`trace-req-toggle-${item.requirement_id}`}
      >
        {open ? <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" /> : <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />}
        <span className="px-2 py-0.5 bg-azure/10 text-azure rounded font-mono text-sm font-medium">{item.requirement_id}</span>
        <span className="text-sm font-medium text-blueprint flex-1 truncate">{item.requirement_title}</span>
        <span className="px-2 py-0.5 bg-muted rounded text-xs font-medium">{item.module}</span>
        {cov.total > 0 && (
          <>
            <CoverageBadge pct={cov.pct} />
            <span className="text-xs text-muted-foreground whitespace-nowrap" data-testid={`req-coverage-${item.requirement_id}`}>
              {cov.covered}/{cov.total} covered
            </span>
          </>
        )}
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          {item.testcase_count} TC{item.testcase_count !== 1 ? 's' : ''}
        </span>
      </button>
      {open && item.scenarios.length > 0 && (
        <div className="border-t pb-3 pt-1">
          {item.scenarios.map((s, i) => (
            <ScenarioRow key={s.scenario_id || `unlinked-${i}`} scenario={s} />
          ))}
        </div>
      )}
      {open && item.scenarios.length === 0 && (
        <div className="border-t px-4 py-3">
          <p className="text-sm text-muted-foreground italic">No scenarios or test cases linked</p>
        </div>
      )}
    </Card>
  );
};

export const TraceabilityTab = ({ projectId, onUpdate }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [genProgress, setGenProgress] = useState(0);
  const [showConfirm, setShowConfirm] = useState(false);
  const pollRef = useRef(null);

  const loadData = async () => {
    try {
      const res = await api.get(`/projects/${projectId}/traceability`);
      setData(res.data);
    } catch {
      toast.error('Failed to load traceability matrix');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); return () => clearInterval(pollRef.current); }, [projectId]);

  const pollJob = (jobId) => {
    pollRef.current = setInterval(async () => {
      try {
        const res = await api.get(`/qa-status/${jobId}`);
        const { status, progress } = res.data;
        setGenProgress(progress || 0);
        if (status === 'completed') {
          clearInterval(pollRef.current);
          setGenerating(false);
          setGenProgress(0);
          toast.success('Missing test cases generated');
          setLoading(true);
          loadData();
          if (onUpdate) onUpdate();
        } else if (status === 'failed') {
          clearInterval(pollRef.current);
          setGenerating(false);
          setGenProgress(0);
          toast.error(res.data.error_message || 'Generation failed');
        }
      } catch {
        clearInterval(pollRef.current);
        setGenerating(false);
        setGenProgress(0);
      }
    }, 3000);
  };

  const handleGenerate = async () => {
    setShowConfirm(false);
    setGenerating(true);
    setGenProgress(0);
    try {
      const res = await api.post(`/projects/${projectId}/generate-missing-testcases`);
      if (res.data.already_covered) {
        setGenerating(false);
        toast.info(res.data.message || 'All scenarios already have test cases');
        return;
      }
      if (res.data.job_id) {
        pollJob(res.data.job_id);
      } else {
        setGenerating(false);
      }
    } catch (err) {
      setGenerating(false);
      const msg = err.response?.data?.detail || 'Failed to start generation';
      toast.error(msg);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-azure" />
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card className="p-12 text-center border-dashed" data-testid="traceability-empty">
        <GitBranch className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
        <h3 className="text-xl font-heading font-semibold mb-2 text-blueprint">No traceability data</h3>
        <p className="text-muted-foreground">Add requirements and generate scenarios & test cases first</p>
      </Card>
    );
  }

  const allScenarios = data.flatMap(r => r.scenarios.filter(s => s.scenario_id !== null));
  const coveredCount = allScenarios.filter(s => s.testcases.length > 0).length;
  const missingCount = allScenarios.length - coveredCount;
  const totalTCs = data.reduce((a, r) => a + r.testcase_count, 0);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-heading font-semibold text-blueprint">Traceability Matrix</h2>
        <div className="flex items-center gap-3">
          <p className="text-sm text-muted-foreground" data-testid="traceability-summary">
            {data.length} requirements · {totalTCs} test cases
          </p>
          {missingCount > 0 && (
            <Button
              onClick={() => setShowConfirm(true)}
              disabled={generating}
              className="gap-2 bg-blueprint hover:bg-blueprint/90"
              data-testid="generate-missing-btn"
            >
              {generating ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4" />
                  Generate Missing TCs
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Progress banner */}
      {generating && (
        <Card className="p-4 mb-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200" data-testid="missing-tc-progress">
          <div className="flex items-center gap-3">
            <Loader2 className="h-5 w-5 animate-spin text-blue-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-blue-700">Generating test cases for uncovered scenarios...</p>
              <div className="mt-1.5 h-1.5 bg-blue-100 rounded-full overflow-hidden">
                <div className="h-full bg-blue-500 transition-all duration-500" style={{ width: `${genProgress}%` }} />
              </div>
            </div>
            <span className="text-xs font-mono text-blue-600">{genProgress}%</span>
          </div>
        </Card>
      )}

      {/* Global coverage bar */}
      {allScenarios.length > 0 && (
        <Card className="p-4 mb-5" data-testid="coverage-summary">
          <div className="flex items-center gap-4 text-sm">
            <span className="font-medium text-slate-700">{allScenarios.length} scenarios</span>
            <span className="text-emerald-600 font-medium">{coveredCount} covered</span>
            {missingCount > 0 && <span className="text-red-600 font-medium">{missingCount} missing</span>}
            <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
              <div
                className={`h-full transition-all ${missingCount === 0 ? 'bg-emerald-500' : 'bg-amber-500'}`}
                style={{ width: `${allScenarios.length > 0 ? (coveredCount / allScenarios.length) * 100 : 0}%` }}
              />
            </div>
            <span className="font-semibold text-slate-700" data-testid="coverage-pct">
              {allScenarios.length > 0 ? Math.round((coveredCount / allScenarios.length) * 100) : 0}%
            </span>
          </div>
        </Card>
      )}

      <div className="space-y-3" data-testid="traceability-list">
        {data.map(item => (
          <RequirementRow key={item.requirement_id} item={item} />
        ))}
      </div>

      {/* Confirmation dialog */}
      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generate Missing Test Cases</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground py-2">
            This will generate test cases only for <span className="font-semibold text-red-600">{missingCount}</span> uncovered scenario{missingCount !== 1 ? 's' : ''}. Existing test cases will not be modified.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirm(false)}>Cancel</Button>
            <Button onClick={handleGenerate} className="gap-2 bg-blueprint hover:bg-blueprint/90" data-testid="confirm-generate-missing">
              <Sparkles className="h-4 w-4" /> Generate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
